import { Chip, Theme, IconButton, CircularProgress, Button } from "@material-ui/core";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core/styles";
import {
  DeleteOutlined as DeleteIcon,
  ReportProblemOutlined as WarningIcon,
  ErrorOutlined as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon,
} from "@material-ui/icons/";
import clsx from "clsx";
import React, { ReactElement, useState, useEffect, useRef } from "react";
import { Flipped } from "react-flip-toolkit";
import { ViImageData, ViCoreWarning, Filter } from "../models/";
import CaseImage from "./Image";
import { Translator } from "../services";
import DamageDetailCard from "./DamageDetailCard";
import DeleteImageCard from "./DeleteImageCard";
import { ViImageStatus } from "../models";

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  listItem: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(50% - 0.382rem)",
      "&.focus, &.fullView": {
        maxWidth: "100%",
      },
    },
    [theme.breakpoints.only("xs")]: {
      maxWidth: "100%",
    },
    position: "relative",
    display: "flex",
    flexFlow: "column nowrap",
    boxSizing: "border-box",
    padding: "0.618rem",
    placeSelf: "flex-start stretch",
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper,
    borderRadius: "0.382rem",
    "& .description": {
      marginTop: "0.382rem",
    },
    "&:not(.focus):not(.fullView)": {
      // maxWidth: "24rem",
    },
    "&.focus": {
      flexFlow: "row nowrap",
      width: "fit-content",
      position: "relative",
      // width: "100%",
      // maxHeight: "50vh",
      zIndex: 1,
      order: -1,
      "& .description": {
        alignSelf: "flex-start",
        height: "calc(100% - 2rem)",
        marginLeft: "0.618rem",
      },
    },
    "&.fullView": {
      position: "fixed",
      top: 0,
      left: 0,
      height: "100%",
      width: "100%",
      zIndex: 10,
      boxSizing: "border-box",
      padding: "0.382rem",
      margin: 0,
      order: -1,
      display: "flex",
      placeContent: "center",
      backgroundColor: theme.palette.background.default,
      "& .description": {
        position: "absolute",
        right: 0,
        top: 0,
      },
    },
    "&.pinned": {
      zIndex: 1,
    },
  },
  pills: {
    marginTop: "0.2rem",
    listStyle: "none",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "0 0.382rem",
    "& li": {
      userSelect: "none",
      display: "flex",
      transition: "all 0.382s ease-out",
      "& + li": {
        marginLeft: "0.382rem",
      },
      "&:hover": {
        zIndex: 1,
      },
    },
  },
  backButton: {
    marginRight: "0.618rem",
  },
  spinner: {
    height: "1.5rem",
  },
  warning: {
    color: theme.palette.warning.main,
    cursor: "pointer",
    ":not(.focus,.fullView) &:hover": {
      color: "black",
    },
  },
  rejected: {
    "& > div": {
      cursor: "pointer",
      fontWeight: 500,
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    "& svg": {
      color: theme.palette.error.contrastText,
    },
  },
  delete: {
    marginLeft: "auto !important",
    "& button": {
      color: theme.palette.error.light,
      padding: 0,
    },
  },
  damageCards: {
    display: "flex",
    flexFlow: "column wrap",
    maxHeight: "100%",
    alignContent: "flex-start",
    "& > div": {
      cursor: "pointer",
    },
  },
  damages: {
    "& > div": {
      cursor: "pointer",
    },
  },
  popover: {
    pointerEvents: "none",
  },
  infoButton: {
    color: theme.palette.grey.A100,
    cursor: "pointer",
  },
  paper: {
    ...theme.typography.button,
    padding: theme.spacing(theme.goldenRatio(1)),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.grey.A700,
    textTransform: "none",
    margin: "0",
    maxWidth: `${theme.goldenRatio(7)}rem`,
  },
  infoPaper: {
    ...theme.typography.button,
    padding: theme.spacing(theme.goldenRatio(1)),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.grey.A700,
    textTransform: "none",
    margin: "0",
    display: "grid",
    gridTemplateColumns: "repeat(2,auto)",
    "& span:nth-of-type(2n)": {
      color: theme.palette.text.hint,
      marginLeft: `${theme.goldenRatio(-1)}rem`,
    },
  },
}));

function ImageCard({
  focus,
  fullView,
  imageData,
  onClick,
  setHoverFilter,
  hoverFilter,
  isLoading,
  onImageDelete,
  final,
  goBack,
  canFlip,
}: {
  focus: boolean;
  fullView: boolean;
  imageData: ViImageData;
  onClick: (imageData: ViImageData, focus: boolean, fullView: boolean) => void;
  setHoverFilter: (filters: Filter[]) => void;
  hoverFilter: Filter[];
  isLoading: boolean;
  onImageDelete: (image: ViImageData) => void;
  final: boolean;
  goBack: () => void;
  canFlip: boolean;
}): ReactElement {
  const classes = useStyles({
    focus,
    fullView,
  });
  const [data, setData] = useState<ViImageData>(imageData);
  const [popover, setPopover] = React.useState<{
    anchorEl: HTMLElement;
    id: string;
  } | null>(null);
  const [toDelete, setToDelete] = useState(false);
  const prevState = useRef<string>("");

  useEffect(() => {
    if (prevState.current === "focus") {
      setToDelete(false);
    }
    prevState.current = (focus && "focus") || (fullView && "fullView") || "";
  }, [focus, fullView]);

  useEffect(() => {
    setData(imageData);
  }, [imageData]);

  function handleClick() {
    onClick(data, focus, fullView);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.preventDefault();

    e.stopPropagation();
    if (!focus) {
      onClick(data, focus, fullView);
    }
    setToDelete(true);
  }

  function handleDeleteConfirm() {
    onImageDelete(data);
  }

  function handleDeleteCancel(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setToDelete(false);
  }

  const handlePopoverOpen = (event: React.MouseEvent<any, MouseEvent>, id: string) => {
    setPopover({ anchorEl: event.currentTarget, id });
  };

  const handlePopoverClose = () => {
    setPopover(null);
  };

  return (
    <Flipped
      flipId={`listItem-${imageData.localId}`}
      onStart={(el, decisionData) => {
        el.classList.add("flipping");
        el?.parentElement?.scrollTo({ top: 0, behavior: "smooth" });
        if (decisionData?.previous.fullViewIndex === data.id) el.classList.add("pinned");
      }}
      onComplete={(el, decisionData) => {
        if (decisionData?.previous.fullViewIndex === data.id)
          el.classList.remove("pinned");
        el.classList.remove("flipping");
      }}
    >
      <li className={clsx(classes.listItem, focus && "focus", fullView && "fullView")}>
        <CaseImage
          clickable={canFlip && (focus || !fullView)}
          onClick={handleClick}
          {...{ data, hoverFilter, setHoverFilter, isLoading }}
        />
        <Flipped flipId={`description-${data.localId}`}>
          <div className="description">
            <ul className={classes.pills}>
              {fullView && (
                <Button
                  size="small"
                  onClick={goBack}
                  startIcon={<ArrowBackIcon />}
                  className={classes.backButton}
                >
                  {Translator.translate("common.back")}
                </Button>
              )}
              {isLoading &&
                !data.isDuplicate &&
                data.status?.toUpperCase() !== ViImageStatus.REJECTED && (
                  <li className={classes.spinner}>
                    <CircularProgress
                      color="secondary"
                      size="1.5rem"
                      thickness={5}
                      disableShrink={true}
                    />
                  </li>
                )}
              {data.status?.toUpperCase() === ViImageStatus.REJECTED && (
                <li className={classes.rejected}>
                  <Chip
                    icon={<ErrorIcon />}
                    size="small"
                    label="Rejected"
                    onMouseEnter={(e) => handlePopoverOpen(e, "rejected")}
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="error-popover"
                    className={classes.popover}
                    classes={{
                      paper: classes.infoPaper,
                    }}
                    open={popover?.id === "rejected"}
                    anchorEl={popover?.anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                  >
                    {data.error && <span>{data.error.description}</span>}
                  </Popover>
                </li>
              )}
              {!data.isDuplicate && data.damages && data.damages.length > 0 && (
                <li className={classes.damages}>
                  <Chip
                    variant="outlined"
                    size="small"
                    label={`${data.damages.length} Damage${
                      data.damages.length > 1 ? "s" : ""
                    }`}
                    onMouseEnter={
                      !focus ? (e) => handlePopoverOpen(e, "damages") : () => {}
                    }
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="damages-popover"
                    className={classes.popover}
                    classes={{
                      paper: classes.paper,
                    }}
                    open={popover?.id === "damages"}
                    anchorEl={popover?.anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                  >
                    <div className={classes.damageCards}>
                      {data.damages.map((damage, idx: number) => {
                        return (
                          <DamageDetailCard
                            key={idx}
                            imgPartDamage={damage}
                            {...{ hoverFilter, setHoverFilter }}
                          />
                        );
                      })}
                    </div>
                  </Popover>
                </li>
              )}
              {data.category?.label && (
                <li>
                  <Chip
                    variant="outlined"
                    size="small"
                    label={Translator.translate(
                      `categories.${data.category.label.toLowerCase()}`
                    )}
                  />
                </li>
              )}
              {data.dimensions?.width && data.dimensions.height && (
                <li className={classes.info}>
                  <InfoIcon
                    className={classes.infoButton}
                    onMouseEnter={(e) => handlePopoverOpen(e, "info")}
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="info-popover"
                    className={classes.popover}
                    classes={{
                      paper: classes.infoPaper,
                    }}
                    open={popover?.id === "info"}
                    anchorEl={popover?.anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                  >
                    <span>{"dimensions: "}</span>
                    <span>{`${data?.dimensions?.width} x ${data?.dimensions?.height}px`}</span>
                    <span>{"filename: "}</span>
                    <span>{`${data.filename}`}</span>
                  </Popover>
                </li>
              )}
              {data.warnings && data.warnings?.length > 0 && (
                <li className={clsx(classes.warning, "warning")}>
                  <WarningIcon
                    onMouseEnter={(e) => handlePopoverOpen(e, "warning")}
                    onMouseLeave={handlePopoverClose}
                  />
                  <Popover
                    id="warnings-popover"
                    className={classes.popover}
                    classes={{
                      paper: classes.paper,
                    }}
                    open={popover?.id === "warning"}
                    anchorEl={popover?.anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableRestoreFocus
                  >
                    {data.warnings &&
                      data.warnings.map((w: ViCoreWarning, i: number) => {
                        return <span key={`warning-${i}`}>{w.description}</span>;
                      })}
                  </Popover>
                </li>
              )}
              {data.status?.toUpperCase() !== ViImageStatus.REJECTED &&
                !isLoading &&
                !toDelete &&
                !fullView &&
                canFlip && (
                  <li className={classes.delete}>
                    <IconButton onClick={handleDeleteClick}>
                      <DeleteIcon />
                    </IconButton>
                  </li>
                )}
            </ul>
            {toDelete
              ? focus && (
                  <Flipped flipId={`damageContainer-${data.localId}`} stagger="cards">
                    <div className={classes.damageCards}>
                      <DeleteImageCard
                        onDelete={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                        final={final}
                      />
                    </div>
                  </Flipped>
                )
              : data.damages &&
                data.damages.length > 0 && (
                  <Flipped flipId={`damageContainer-${data.localId}`} stagger="cards">
                    <div className={classes.damageCards}>
                      {data.damages.map((damage, idx: number) => {
                        return (
                          <DamageDetailCard
                            key={idx}
                            imgPartDamage={damage}
                            {...{
                              hoverFilter,
                              setHoverFilter,
                              focus,
                              fullView,
                            }}
                          />
                        );
                      })}
                    </div>
                  </Flipped>
                )}
          </div>
        </Flipped>
      </li>
    </Flipped>
  );
}

export default React.memo(ImageCard, (prev, next) => {
  const hasDamages =
    next.imageData && next.imageData.damages && next.imageData.damages.length < 1;
  return (
    prev.focus === next.focus &&
    prev.fullView === next.fullView &&
    prev.isLoading === next.isLoading &&
    prev.imageData.status === next.imageData.status &&
    prev.imageData.data === next.imageData.data &&
    prev.onClick === next.onClick &&
    (hasDamages || prev.hoverFilter === next.hoverFilter)
  );
});
